import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../api/axios';
import {
  Container,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import RoomDimensionsFields from '../components/project/RoomDimensionsFields';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  PictureAsPdf as PdfIcon,
  Edit as EditIcon,
  CloudUpload as UploadIcon,
  History as HistoryIcon,
  Bookmark as BookmarkIcon,
} from '@mui/icons-material';
import RevisionDiffDialog from '../components/RevisionDiffDialog';

const EstimateBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showRoomsReminder, setShowRoomsReminder] = useState(Boolean(location.state?.justCreated));
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialog states
  const [roomDialog, setRoomDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  // True when the dialog was triggered by Submit (post-submit auto-prompt).
  // Closing should navigate back to dashboard. False when triggered by the
  // manual Save as Template button — closing should keep editing.
  const [templateAfterSubmit, setTemplateAfterSubmit] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', description: '', type: 'room', room_ids: [], square_footage: '', ceiling_height: '' });
  const [newItem, setNewItem] = useState({
    room_id: '',
    title: '',
    type: 'materials',
    cost: '',
    image_url: '',
  });
  const [calcMode, setCalcMode] = useState('none'); // 'none' | 'sqft' | 'lnft' | 'sqyd'
  const [calcData, setCalcData] = useState({ pricePerUnit: '', totalUnits: '' });
  const [uploadingImage, setUploadingImage] = useState(false);

  const calcModeConfig = {
    sqft: { label: 'Square Footage', priceLabel: 'Price per Sq Ft', unitLabel: 'Total Sq Ft' },
    lnft: { label: 'Linear Feet', priceLabel: 'Price per Linear Ft', unitLabel: 'Total Linear Ft' },
    sqyd: { label: 'Square Yards', priceLabel: 'Price per Sq Yard', unitLabel: 'Total Sq Yards' },
  };

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/estimates/${id}`);
      setEstimate(response.data.estimate);
    } catch (error) {
      setError('Failed to load estimate');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  // Drop the justCreated flag so a page refresh doesn't re-trigger the rooms reminder.
  useEffect(() => {
    if (location.state?.justCreated) {
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateRoom = async () => {
    try {
      await axios.post('/api/rooms', {
        project_id: id,
        name: newRoom.name,
        type: newRoom.type,
        description: newRoom.description,
        room_ids: newRoom.type === 'work_area' ? newRoom.room_ids : undefined,
        square_footage: newRoom.type === 'room' && newRoom.square_footage !== '' ? newRoom.square_footage : null,
        ceiling_height: newRoom.type === 'room' && newRoom.ceiling_height !== '' ? newRoom.ceiling_height : null,
      });
      setRoomDialog(false);
      setNewRoom({ name: '', description: '', type: 'room', room_ids: [], square_footage: '', ceiling_height: '' });
      fetchEstimate();
    } catch (error) {
      setError('Failed to create room');
      console.error('Error:', error);
    }
  };

  const handleCreateItem = async () => {
    try {
      const cost = calcMode !== 'none'
        ? parseFloat(calcData.pricePerUnit) * parseFloat(calcData.totalUnits)
        : parseFloat(newItem.cost);

      await axios.post('/api/items', {
        ...newItem,
        cost: cost
      });

      setItemDialog(false);
      setNewItem({
        room_id: '',
        title: '',
        type: 'materials',
        cost: '',
        image_url: '',
      });
      setCalcMode('none');
      setCalcData({ pricePerUnit: '', totalUnits: '' });
      fetchEstimate();
    } catch (error) {
      setError('Failed to create item');
      console.error('Error:', error);
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setNewItem({
      room_id: item.room_id,
      title: item.title,
      type: item.type,
      cost: item.cost.toString(),
      image_url: item.image_url || '',
    });
    setItemDialog(true);
  };

  const handleUpdateItem = async () => {
    try {
      const cost = calcMode !== 'none'
        ? parseFloat(calcData.pricePerUnit) * parseFloat(calcData.totalUnits)
        : parseFloat(newItem.cost);

      await axios.put(`/api/items/${editingItem.id}`, {
        title: newItem.title,
        type: newItem.type,
        cost: cost,
        image_url: newItem.image_url,
      });

      setItemDialog(false);
      setEditingItem(null);
      setNewItem({
        room_id: '',
        title: '',
        type: 'materials',
        cost: '',
        image_url: '',
      });
      setCalcMode('none');
      setCalcData({ pricePerUnit: '', totalUnits: '' });
      fetchEstimate();
    } catch (error) {
      setError('Failed to update item');
      console.error('Error:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await axios.delete(`/api/items/${itemId}`);
        fetchEstimate();
      } catch (error) {
        setError('Failed to delete item');
        console.error('Error:', error);
      }
    }
  };

  const handleDeleteRoom = async (roomId) => {
    if (window.confirm('Are you sure you want to delete this room and all its items?')) {
      try {
        await axios.delete(`/api/rooms/${roomId}`);
        fetchEstimate();
      } catch (error) {
        setError('Failed to delete room');
        console.error('Error:', error);
      }
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    setUploadingImage(true);
    try {
      const { data } = await axios.post('/api/items/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewItem({ ...newItem, image_url: data.url });
    } catch (err) {
      setError('Failed to upload image');
      console.error('Error:', err);
    } finally {
      setUploadingImage(false);
    }
    e.target.value = ''; // reset so re-selecting the same file triggers onChange
  };

  const handleSubmitEstimate = async () => {
    if (window.confirm('Submit this estimate to the homeowner for approval? You will not be able to edit it while it is pending approval.')) {
      try {
        await axios.put(`/api/estimates/${id}/submit`);
        // Offer to save as template after a successful submit. Doing this
        // post-submit (vs offering a button at any time) catches contractors
        // when they're most likely to want to reuse this work shape.
        setTemplateAfterSubmit(true);
        setTemplateName(estimate?.name || '');
        setSaveTemplateOpen(true);
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to submit estimate');
        console.error('Error:', error);
      }
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      await axios.post(`/api/projects/${id}/save-as-template`, { name: templateName.trim() });
      setSaveTemplateOpen(false);
      if (templateAfterSubmit) {
        navigate('/');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleSkipTemplate = () => {
    setSaveTemplateOpen(false);
    if (templateAfterSubmit) {
      navigate('/');
    }
    setTemplateAfterSubmit(false);
  };

  const handleDownloadPDF = () => {
    // Open print view in new window - user can save as PDF from browser
    window.open(`/estimate-print/${id}`, '_blank');
  };

  const handleDeleteEstimate = async () => {
    if (!window.confirm(`Delete estimate "${estimate?.name || ''}"? This permanently removes the estimate and all its rooms and items. This cannot be undone.`)) return;
    try {
      await axios.delete(`/api/estimates/${id}`);
      navigate('/');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete estimate');
    }
  };

  const getSubtotal = () => {
    if (!estimate || !estimate.rooms) return 0;
    return estimate.rooms.reduce((total, room) => {
      const roomTotal = room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
      return total + roomTotal;
    }, 0);
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const taxRate = parseFloat(estimate?.tax_rate || 0);
    return (subtotal * taxRate) / 100;
  };

  const getTotalCost = () => {
    return getSubtotal() + getTaxAmount();
  };

  const handleTaxRateChange = async (newTaxRate) => {
    try {
      const parsedRate = parseFloat(newTaxRate) || 0;

      // Optimistically update UI first
      setEstimate({ ...estimate, tax_rate: parsedRate });

      // Then save to backend
      await axios.put(`/api/estimates/${id}`, {
        tax_rate: parsedRate
      });
    } catch (error) {
      const errorMsg = error.response?.data?.error || 'Failed to update tax rate';
      const errorDetails = error.response?.data?.details;

      console.error('Tax rate update error:', error);
      console.error('Error response:', error.response?.data);

      setError(errorDetails ? `${errorMsg}: ${errorDetails}` : errorMsg);

      // Revert on error
      fetchEstimate();
    }
  };

  const getTotalItems = () => {
    if (!estimate || !estimate.rooms) return 0;
    return estimate.rooms.reduce((total, room) => total + room.items.length, 0);
  };

  const getStatusColor = () => {
    switch (estimate?.status) {
      case 'estimate': return 'warning';
      case 'pending_approval': return 'info';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = () => {
    switch (estimate?.status) {
      case 'estimate': return 'Draft';
      case 'pending_approval': return 'Pending Approval';
      case 'rejected': return 'Rejected';
      default: return estimate?.status;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!estimate) {
    return (
      <Container>
        <Alert severity="error">Estimate not found</Alert>
      </Container>
    );
  }

  const canEdit = estimate.status === 'estimate' || estimate.status === 'rejected';
  const isPendingApproval = estimate.status === 'pending_approval';
  const isRejected = estimate.status === 'rejected';

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 2, color: 'primary.main' }} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" color="primary">
              {estimate.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Estimate for {estimate.homeowner_first_name} {estimate.homeowner_last_name}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel()}
            color={getStatusColor()}
            sx={{ mr: 2 }}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<HistoryIcon />}
            onClick={() => setRevisionsOpen(true)}
            sx={{ mr: 2 }}
          >
            Revisions
          </Button>
          {canEdit && getTotalItems() > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<BookmarkIcon />}
              onClick={() => { setTemplateName(estimate?.name || ''); setSaveTemplateOpen(true); }}
              sx={{ mr: 2 }}
            >
              Save as Template
            </Button>
          )}
          {getTotalItems() > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={handleDownloadPDF}
              sx={{ mr: 2 }}
            >
              Download PDF
            </Button>
          )}
          {canEdit && getTotalItems() > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSubmitEstimate}
            >
              Submit to Homeowner
            </Button>
          )}
          {canEdit && !['active', 'completed'].includes(estimate?.status) && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteEstimate}
              sx={{ ml: 2 }}
            >
              Delete Estimate
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {isPendingApproval && (
          <Alert severity="info" sx={{ mb: 3 }}>
            This estimate is pending homeowner approval. You cannot edit it while it's being reviewed.
          </Alert>
        )}

        {isRejected && estimate.rejection_reason && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <strong>Rejected:</strong> {estimate.rejection_reason}
            <br />
            <Typography variant="caption">You can edit and resubmit this estimate.</Typography>
          </Alert>
        )}

        {/* Estimate Summary */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h5" gutterBottom>Estimate Summary</Typography>
          <Box display="flex" gap={4} mt={2} mb={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">Subtotal</Typography>
              <Typography variant="h4" color="text.primary" fontWeight="bold">
                ${getSubtotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Total Items</Typography>
              <Typography variant="h4">{getTotalItems()}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Rooms/Work Areas</Typography>
              <Typography variant="h4">{estimate.rooms?.length || 0}</Typography>
            </Box>
          </Box>

          {/* Tax Rate Input */}
          {canEdit && (
            <Box mb={2}>
              <TextField
                label="Tax Rate (%)"
                type="number"
                value={estimate?.tax_rate || 0}
                onChange={(e) => setEstimate({ ...estimate, tax_rate: e.target.value })}
                onBlur={(e) => handleTaxRateChange(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                sx={{ width: 200 }}
                size="small"
              />
            </Box>
          )}

          {/* Tax and Total */}
          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            {parseFloat(estimate?.tax_rate || 0) > 0 && (
              <Box display="flex" gap={2} alignItems="center">
                <Typography variant="body1" color="text.secondary">
                  Tax ({parseFloat(estimate?.tax_rate || 0).toFixed(2)}%):
                </Typography>
                <Typography variant="h6">
                  ${getTaxAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
            <Box display="flex" gap={2} alignItems="center">
              <Typography variant="h6" color="text.secondary">
                Total Cost:
              </Typography>
              <Typography variant="h4" color="primary" fontWeight="bold">
                ${getTotalCost().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Rooms and Items */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h5">Rooms & Work Areas</Typography>
          {canEdit && (
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewRoom({ name: '', description: '', type: 'room', room_ids: [] });
                  setRoomDialog(true);
                }}
              >
                Add Room
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewRoom({ name: '', description: '', type: 'work_area', room_ids: [] });
                  setRoomDialog(true);
                }}
                disabled={!estimate?.rooms || estimate.rooms.filter(r => r.type === 'room').length === 0}
              >
                Add Work Area
              </Button>
            </Box>
          )}
        </Box>
        {canEdit && (
          <Typography variant="body2" color="text.secondary" mb={3}>
            Add all of this project's rooms first, then group them into work areas whenever it helps.
          </Typography>
        )}

        {estimate.rooms && estimate.rooms.length > 0 ? (
          estimate.rooms.map((room) => (
            <Accordion key={room.id} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
                  <Box>
                    <Typography variant="h6">{room.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {room.items.length} items - $
                      {room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0).toLocaleString()}
                      {(() => {
                        const dims = [];
                        const sqft = parseFloat(room.square_footage);
                        const h = parseFloat(room.ceiling_height);
                        if (Number.isFinite(sqft) && sqft > 0) dims.push(`${sqft} sq ft`);
                        if (Number.isFinite(h) && h > 0) dims.push(`${h} ft ceiling`);
                        return dims.length > 0 ? ` · ${dims.join(' · ')}` : '';
                      })()}
                    </Typography>
                  </Box>
                  {canEdit && (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteRoom(room.id);
                      }}
                      color="error"
                      size="small"
                      aria-label={`Delete room: ${room.name}`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {canEdit && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => {
                        setNewItem({ ...newItem, room_id: room.id });
                        setItemDialog(true);
                      }}
                      sx={{ mb: 2 }}
                    >
                      Add Item
                    </Button>
                  )}

                  {room.items.map((item) => (
                    <Paper key={item.id} sx={{ p: 2, mb: 2 }} variant="outlined">
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box flex={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {item.title}
                          </Typography>
                          <Chip label={item.type} size="small" sx={{ mt: 1 }} />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" color="primary" fontWeight="bold">
                            ${parseFloat(item.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Typography>
                          {canEdit && (
                            <>
                              <IconButton
                                onClick={() => handleEditItem(item)}
                                color="primary"
                                size="small"
                                aria-label={`Edit item: ${item.title}`}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() => handleDeleteItem(item.id)}
                                color="error"
                                size="small"
                                aria-label={`Delete item: ${item.title}`}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </Box>
                    </Paper>
                  ))}

                  {room.items.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No items yet. Add items to this room to build your estimate.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No rooms added yet. Create rooms and add items to build your estimate.
            </Typography>
          </Paper>
        )}
      </Container>

      {/* Add-all-rooms reminder, shown right after creating an estimate */}
      <Dialog open={showRoomsReminder} onClose={() => setShowRoomsReminder(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add your rooms first</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Add all of this estimate's rooms now, up front. Once your rooms are in, you can
            group them into work areas anytime.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowRoomsReminder(false)}>Got it</Button>
        </DialogActions>
      </Dialog>

      {/* Create Room Dialog */}
      <Dialog open={roomDialog} onClose={() => setRoomDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{newRoom.type === 'work_area' ? 'Add Work Area' : 'Add Room'}</DialogTitle>
        <DialogContent>
          {newRoom.type === 'work_area' && (
            <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
              A work area groups existing rooms together for organization. Select which rooms below.
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label={newRoom.type === 'room' ? 'Room Name' : 'Work Area Name'}
            fullWidth
            variant="outlined"
            value={newRoom.name}
            onChange={(e) => setNewRoom({ ...newRoom, name: e.target.value })}
            sx={{ mb: 2, mt: newRoom.type === 'room' ? 1 : 0 }}
            placeholder={newRoom.type === 'room' ? 'e.g., Kitchen, Master Bedroom' : 'e.g., Main Floor Flooring, Upstairs Drywall'}
          />

          {newRoom.type === 'room' && (
            <RoomDimensionsFields
              sx={{ mb: 2 }}
              squareFootage={newRoom.square_footage}
              onSquareFootageChange={(v) => setNewRoom({ ...newRoom, square_footage: v })}
              ceilingHeight={newRoom.ceiling_height}
              onCeilingHeightChange={(v) => setNewRoom({ ...newRoom, ceiling_height: v })}
            />
          )}

          {newRoom.type === 'work_area' && estimate?.rooms && estimate.rooms.filter(r => r.type === 'room').length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Select Rooms to Include
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block" sx={{ mb: 1 }}>
                Choose which rooms are part of this work area
              </Typography>
              <FormGroup>
                {estimate.rooms.filter(r => r.type === 'room').map((room) => (
                  <FormControlLabel
                    key={room.id}
                    control={
                      <Checkbox
                        checked={newRoom.room_ids.includes(room.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewRoom({ ...newRoom, room_ids: [...newRoom.room_ids, room.id] });
                          } else {
                            setNewRoom({ ...newRoom, room_ids: newRoom.room_ids.filter(id => id !== room.id) });
                          }
                        }}
                      />
                    }
                    label={room.name}
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newRoom.description}
            onChange={(e) => setNewRoom({ ...newRoom, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateRoom}
            variant="contained"
            disabled={!newRoom.name.trim()}
          >
            {newRoom.type === 'work_area' ? 'Add Work Area' : 'Add Room'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Item Dialog */}
      <Dialog open={itemDialog} onClose={() => {
        setItemDialog(false);
        setEditingItem(null);
        setNewItem({ room_id: '', title: '', type: 'materials', cost: '', image_url: '' });
        setCalcMode('none');
        setCalcData({ pricePerUnit: '', totalUnits: '' });
      }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Item' : 'Add Item'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Item Title"
            fullWidth
            variant="outlined"
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={newItem.type}
              onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
              label="Type"
            >
              <MenuItem value="labor">Labor</MenuItem>
              <MenuItem value="materials">Materials</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Pricing Method</InputLabel>
            <Select
              value={calcMode}
              onChange={(e) => {
                setCalcMode(e.target.value);
                setCalcData({ pricePerUnit: '', totalUnits: '' });
                setNewItem({ ...newItem, cost: '' });
              }}
              label="Pricing Method"
            >
              <MenuItem value="none">Enter Cost Directly</MenuItem>
              <MenuItem value="sqft">Calculate by Square Footage</MenuItem>
              <MenuItem value="lnft">Calculate by Linear Feet</MenuItem>
              <MenuItem value="sqyd">Calculate by Square Yards</MenuItem>
            </Select>
          </FormControl>

          {calcMode !== 'none' ? (
            <>
              <TextField
                margin="dense"
                label={calcModeConfig[calcMode].priceLabel}
                fullWidth
                variant="outlined"
                type="number"
                value={calcData.pricePerUnit}
                onChange={(e) => setCalcData({ ...calcData, pricePerUnit: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label={calcModeConfig[calcMode].unitLabel}
                fullWidth
                variant="outlined"
                type="number"
                value={calcData.totalUnits}
                onChange={(e) => setCalcData({ ...calcData, totalUnits: e.target.value })}
                sx={{ mb: 2 }}
              />
              {calcData.pricePerUnit && calcData.totalUnits && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Total: ${(parseFloat(calcData.pricePerUnit) * parseFloat(calcData.totalUnits)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              )}
            </>
          ) : (
            <TextField
              margin="dense"
              label="Cost"
              fullWidth
              variant="outlined"
              type="number"
              value={newItem.cost}
              onChange={(e) => setNewItem({ ...newItem, cost: e.target.value })}
              sx={{ mb: 2 }}
            />
          )}

          {/* Image upload */}
          <Box sx={{ mb: 2 }}>
            <input
              type="file"
              id="item-image-upload"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => document.getElementById('item-image-upload').click()}
              disabled={uploadingImage}
              sx={{ mr: 1 }}
            >
              {uploadingImage ? 'Uploading…' : newItem.image_url ? 'Change Photo' : 'Add Photo'}
            </Button>
            {newItem.image_url && (
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={() => setNewItem({ ...newItem, image_url: '' })}
              >
                Remove
              </Button>
            )}
            {newItem.image_url && (
              <Box
                component="img"
                src={newItem.image_url.startsWith('http') ? newItem.image_url : `${process.env.REACT_APP_API_URL || 'https://congrow-beta-production.up.railway.app'}${newItem.image_url}`}
                alt="Item preview"
                sx={{ display: 'block', mt: 1, maxWidth: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setItemDialog(false);
            setEditingItem(null);
            setNewItem({ room_id: '', title: '', type: 'materials', cost: '', image_url: '' });
            setCalcMode('none');
            setCalcData({ pricePerUnit: '', totalUnits: '' });
          }}>Cancel</Button>
          <Button
            onClick={editingItem ? handleUpdateItem : handleCreateItem}
            variant="contained"
            disabled={
              !newItem.title.trim() ||
              (calcMode === 'none' && !newItem.cost) ||
              (calcMode !== 'none' && (!calcData.pricePerUnit || !calcData.totalUnits))
            }
          >
            {editingItem ? 'Update Item' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      <RevisionDiffDialog
        open={revisionsOpen}
        onClose={() => setRevisionsOpen(false)}
        estimateId={id}
      />

      <Dialog open={saveTemplateOpen} onClose={handleSkipTemplate} maxWidth="sm" fullWidth>
        <DialogTitle>
          {templateAfterSubmit ? 'Save this as a template?' : 'Save as template'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {templateAfterSubmit
              ? 'Estimate submitted. Want to save its room/item structure as a reusable template? Next time you start a similar estimate (another kitchen, another bathroom remodel), you can spin it up from this template in one click instead of rebuilding from scratch.'
              : 'Saves a snapshot of every room and item in this estimate. You can spin up a new estimate from it later — rooms, items, costs, and dimensions copy over with fresh approvals.'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Template name"
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g. Standard Kitchen Remodel"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSkipTemplate} disabled={savingTemplate}>
            {templateAfterSubmit ? 'No thanks' : 'Cancel'}
          </Button>
          <Button
            onClick={handleSaveAsTemplate}
            variant="contained"
            disabled={!templateName.trim() || savingTemplate}
          >
            {savingTemplate ? 'Saving…' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstimateBuilder;
